import { Component } from '@theme/component';
import { slugify } from '@theme/utilities';

/**
 * A custom element that dynamically builds a table of contents
 * from the H2 headings found in .blog-post-content.
 *
 * @extends {Component}
 */
class TableOfContent extends Component {
  #abortController = new AbortController();

  /** @type {HTMLHeadingElement[]} */
  #headings = [];

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      this.#build();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #build() {
    const contentEl = document.querySelector('.blog-post-content');
    if (!contentEl) return;

    this.#headings = Array.from(contentEl.querySelectorAll('h2'));
    if (this.#headings.length === 0) return;

    // Assign an id to each heading based on its text content
    this.#headings.forEach((heading, id) => {
      if (!heading.id) {
        heading.id = slugify(heading.textContent.trim()) + '-' + id;
      }
    });

    const list = document.createElement('ul');
    list.className = 'table-of-content__list';

    this.#headings.forEach((heading) => {
      const item = document.createElement('li');
      item.className = 'table-of-content__item';

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.className = 'table-of-content__link';

      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.#scrollToHeading(heading);
        },
        { signal: this.#abortController.signal }
      );

      item.appendChild(link);
      list.appendChild(item);
    });

    this.appendChild(list);
    this.#observeHeadings();
  }

  /**
   * Scrolls to the target heading, accounting for the sticky header height.
   * @param {HTMLHeadingElement} heading
   */
  #scrollToHeading(heading) {
    const headerHeight =  parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height') || '0');

    console.log(headerHeight)

    const offset = headerHeight + 32;
    const top = heading.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  /**
   * Uses IntersectionObserver to highlight the active heading in the TOC.
   */
  #observeHeadings() {
    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0'
    );

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (intersecting.length > 0) {
          this.#setActiveLink(intersecting[0].target.id);
        }
      },
      {
        rootMargin: `-${headerHeight + 16}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    this.#headings.forEach((heading) => observer.observe(heading));
  }

  /**
   * Marks the link corresponding to the given heading id as active.
   * @param {string} id
   */
  #setActiveLink(id) {
    this.querySelectorAll('.table-of-content__item--active').forEach((el) => {
      el.classList.remove('table-of-content__item--active');
    });

    this.querySelector(`.table-of-content__link[href="#${CSS.escape(id)}"]`)
      ?.closest('.table-of-content__item')
      ?.classList.add('table-of-content__item--active');
  }
}

if (!customElements.get('table-of-content')) {
  customElements.define('table-of-content', TableOfContent);
}